import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronUp, Save } from 'lucide-react';

import { AdminLayout } from '../../components/layout/AdminLayout';
import { AdminRoute } from '../../components/admin/AdminRoute';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

// Types (should match mobile types)
type SurveyQuestionType = 'text' | 'select' | 'recording' | 'text_or_recording';

interface SurveyOption {
  label: string;
  value: string;
}

interface SurveyQuestion {
  id: string;
  type: SurveyQuestionType;
  label: string;
  placeholder?: string;
  options?: SurveyOption[];
  required?: boolean;
  anonymous?: boolean;
  companyContext?: boolean;
  description?: string;
}

export default function SurveyBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const surveyType = (searchParams.get('type') as 'initial_signup' | 'owner_onboarding') || 'initial_signup';

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [surveyName, setSurveyName] = useState('');
  const [showSurvey, setShowSurvey] = useState(true);
  const [requireOneTime, setRequireOneTime] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const addQuestion = () => {
    const newQuestion: SurveyQuestion = {
      id: `question_${Date.now()}`,
      type: 'text',
      label: 'New Question',
      placeholder: 'Enter question text',
      required: false,
    };
    setQuestions([...questions, newQuestion]);
    setExpandedQuestions(new Set([...expandedQuestions, newQuestion.id]));
  };

  const removeQuestion = (id: string) => {
    if (confirm('Are you sure you want to remove this question?')) {
      setQuestions(questions.filter(q => q.id !== id));
      const newExpanded = new Set(expandedQuestions);
      newExpanded.delete(id);
      setExpandedQuestions(newExpanded);
    }
  };

  const updateQuestion = (id: string, updates: Partial<SurveyQuestion>) => {
    setQuestions(questions.map(q => (q.id === id ? { ...q, ...updates } : q)));
  };

  const toggleQuestionExpanded = (id: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleSave = async () => {
    if (!surveyName.trim()) {
      alert('Please enter a survey name');
      return;
    }

    if (questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    // TODO: Implement upload using hooks (similar to mobile)
    alert('Survey upload functionality will be implemented with hooks');
  };

  return (
    <AdminRoute>
      <AdminLayout>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 2xl:px-10 pt-2 sm:pt-3 md:pt-4 lg:pt-5 xl:pt-6 pb-8">
          <div className="mb-6">
            <h1 className="text-[#EBFFEE] text-3xl font-bold mb-2">Survey Builder</h1>
            <p className="text-[#A9A3A3]">
              {surveyType === 'initial_signup' ? 'Initial Signup Survey' : 'Owner Onboarding Survey'}
            </p>
          </div>

          {/* Survey Settings */}
          <Card className="mb-6 bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE]">Survey Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Survey Name
                </label>
                <Input
                  value={surveyName}
                  onChange={e => setSurveyName(e.target.value)}
                  placeholder="Enter survey name"
                  className="bg-[#111010] border-[#504A4A] text-[#EBFFEE]"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                    Show Survey
                  </label>
                  <p className="text-xs text-[#A9A3A3]">Display this survey to users</p>
                </div>
                <input
                  type="checkbox"
                  checked={showSurvey}
                  onChange={e => setShowSurvey(e.target.checked)}
                  className="w-4 h-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                    Require One Time
                  </label>
                  <p className="text-xs text-[#A9A3A3]">Users must complete this survey once</p>
                </div>
                <input
                  type="checkbox"
                  checked={requireOneTime}
                  onChange={e => setRequireOneTime(e.target.checked)}
                  className="w-4 h-4"
                />
              </div>
            </CardContent>
          </Card>

          {/* Questions */}
          <Card className="mb-6 bg-[#221F1F] border-[#504A4A]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-[#EBFFEE]">Questions ({questions.length})</CardTitle>
                <CardDescription className="text-[#A9A3A3]">
                  Add and configure survey questions
                </CardDescription>
              </div>
              <Button
                onClick={addQuestion}
                variant="red"
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </Button>
            </CardHeader>
            <CardContent>
              {questions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#A9A3A3] mb-2">No questions yet</p>
                  <p className="text-sm text-[#706A6A]">Add your first question to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <QuestionEditor
                      key={question.id}
                      question={question}
                      index={index}
                      isExpanded={expandedQuestions.has(question.id)}
                      onToggleExpanded={() => toggleQuestionExpanded(question.id)}
                      onUpdate={updates => updateQuestion(question.id, updates)}
                      onRemove={() => removeQuestion(question.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
              className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
            >
              Cancel
            </Button>
            <Button
              variant="red"
              onClick={handleSave}
              disabled={!surveyName.trim() || questions.length === 0}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Survey
            </Button>
          </div>
        </div>
      </AdminLayout>
    </AdminRoute>
  );
}

interface QuestionEditorProps {
  question: SurveyQuestion;
  index: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onUpdate: (updates: Partial<SurveyQuestion>) => void;
  onRemove: () => void;
}

function QuestionEditor({
  question,
  index,
  isExpanded,
  onToggleExpanded,
  onUpdate,
  onRemove,
}: QuestionEditorProps) {
  return (
    <div className="bg-[#111010] border border-[#504A4A] rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1A1818]"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-3 flex-1">
          <span className="text-sm font-bold text-rollops-accent">Q{index + 1}</span>
          <span className="text-[#EBFFEE] flex-1">{question.label || 'Untitled Question'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#A9A3A3] uppercase">{question.type}</span>
          {question.required && (
            <span className="px-2 py-1 text-xs bg-[#9B1421] text-white rounded">Required</span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-[#A9A3A3]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#A9A3A3]" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 border-t border-[#504A4A] space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Question Label</label>
            <textarea
              value={question.label}
              onChange={e => onUpdate({ label: e.target.value })}
              placeholder="Enter question text"
              className="w-full min-h-[80px] px-3 py-2 bg-[#111010] border border-[#504A4A] rounded-md text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Question Type</label>
            <div className="flex gap-2 flex-wrap">
              {(['text', 'select', 'recording', 'text_or_recording'] as SurveyQuestionType[]).map(
                type => (
                  <button
                    key={type}
                    onClick={() => onUpdate({ type })}
                    className={`px-3 py-1 rounded text-sm ${
                      question.type === type
                        ? 'bg-[#9B1421] text-white'
                        : 'bg-[#221F1F] text-[#A9A3A3] border border-[#504A4A] hover:bg-[#363232]'
                    }`}
                  >
                    {type.replace('_', ' ')}
                  </button>
                ),
              )}
            </div>
          </div>

          {question.type === 'select' && (
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Options (one per line, format: Label|value)
              </label>
              <textarea
                value={question.options?.map(o => `${o.label}|${o.value}`).join('\n') || ''}
                onChange={e => {
                  const options = e.target.value
                    .split('\n')
                    .filter(line => line.trim())
                    .map(line => {
                      const [label, value] = line.split('|');
                      return {
                        label: label?.trim() || line.trim(),
                        value: value?.trim() || line.trim().toLowerCase().replace(/\s+/g, '_'),
                      };
                    });
                  onUpdate({ options });
                }}
                placeholder="Option 1|value1&#10;Option 2|value2"
                className="w-full min-h-[100px] px-3 py-2 bg-[#111010] border border-[#504A4A] rounded-md text-[#EBFFEE] focus:outline-none focus:border-[#9B1421] font-mono text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Placeholder (optional)
            </label>
            <Input
              value={question.placeholder || ''}
              onChange={e => onUpdate({ placeholder: e.target.value })}
              placeholder="Enter placeholder text"
              className="bg-[#111010] border-[#504A4A] text-[#EBFFEE]"
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-[#EBFFEE]">Required</label>
            <input
              type="checkbox"
              checked={question.required || false}
              onChange={e => onUpdate({ required: e.target.checked })}
              className="w-4 h-4"
            />
          </div>

          <Button
            variant="destructive"
            size="sm"
            onClick={onRemove}
            className="flex items-center gap-2 w-full"
          >
            <Trash2 className="w-4 h-4" />
            Remove Question
          </Button>
        </div>
      )}
    </div>
  );
}
